import React, { useState, useRef } from 'react';
import { Container, Row, Col, Image, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import youthPoster from '../assets/images/youth-bootCamp-poster.jpg';
import couplesPoster from '../assets/images/couples-conference/couples-conference-poster.jpg';
import woncPoster from '../assets/images/women-of-noble-character/women-conference-save-date.jpg';
import youthConferencePoster from '../assets/images/youth-conference/youth-conference-poster.jpeg';

const posterMap = {
  'youth-bootcamp': youthPoster,
  'youth-conference': youthConferencePoster,
  'couples-conference': couplesPoster,
  'women-of-noble-character': woncPoster,
};

const PosterBanner = ({ event }) => {
  const navigate = useNavigate();
  const posterImage = posterMap[event?.id] || youthPoster;
  const [showModal, setShowModal] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = posterImage;
    link.download = `${event.id}-poster.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${event.title} Poster`,
          text: `Check out this ${event.title}: ${event.subtitle}`,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      alert('Web Share API not supported in your browser. Try downloading the poster instead.');
    }
  };

  // Mobile pinch zoom handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      imageRef.current = { distance };
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setStartPosition({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && imageRef.current) {
      e.preventDefault(); // Prevent default to stop page scrolling
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      
      const newScale = scale * (distance / imageRef.current.distance);
      setScale(Math.min(Math.max(1, newScale), 4)); // Limit scale between 1 and 4
      imageRef.current.distance = distance;
    } else if (e.touches.length === 1 && isDragging) {
      setPosition({
        x: e.touches[0].clientX - startPosition.x,
        y: e.touches[0].clientY - startPosition.y
      });
    }
  };

  const handleTouchEnd = () => {
    imageRef.current = null;
    setIsDragging(false);
    
    // Reset position and scale if zoomed out
    if (scale <= 1.1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleDoubleTap = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
  };

  return (
    <section className="poster-banner py-4 bg-dark">
      <Container>
        <Row className="justify-content-center">
          <Col md={10} lg={8} className="text-center">
            <div className="poster-container position-relative">
              <div 
                className="mobile-zoom-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={handleDoubleTap}
              >
                <Image 
                  src={posterImage} 
                  alt={`${event.title} Poster`} 
                  className="img-fluid poster-image shadow" 
                  onClick={() => setShowModal(true)}
                  style={{ 
                    cursor: 'pointer', 
                    maxHeight: '80vh',
                    transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease'
                  }}
                />
              </div>
              <div className="poster-overlay d-flex justify-content-center align-items-center">
                <Button 
                  variant="light" 
                  size="sm" 
                  className="me-2"
                  onClick={() => setShowModal(true)}
                >
                  <i className="bi bi-zoom-in me-1"></i> View Full Size
                </Button>
              </div>
            </div>
            
            <div className="poster-actions mt-3">
              <Button 
                variant="primary" 
                className="me-2"
                onClick={() => navigate(`/register/${event.id}`)}
              >
                <i className="bi bi-person-plus me-1"></i> Register Now
              </Button>
              <Button 
                variant="warning" 
                onClick={handleShare}
              >
                <i className="bi bi-share me-1"></i> Share Poster
              </Button>
            </div>
            <div className="mobile-instructions d-md-none mt-2">
              <small className="text-light">
                <i className="bi bi-info-circle me-1"></i> 
                Pinch to zoom, double-tap to expand
              </small>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Modal for expanded view */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        size="lg" 
        centered
        fullscreen="md-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>{event.title} Poster</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 modal-pinch-zoom" 
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Image 
            src={posterImage} 
            alt={`${event.title} Poster`} 
            className="img-fluid" 
            style={{ 
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease'
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleDownload}>
            <i className="bi bi-download me-1"></i> Download
          </Button>
          <Button variant="success" onClick={handleShare}>
            <i className="bi bi-share me-1"></i> Share
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default PosterBanner;